import type { AdminJobDetail, CommissionSightClient } from '@commissionsight/sdk';
import { explainJobError, formatPeriodLong } from '@commissionsight/shared';
import { Fragment, type ReactNode, useCallback, useState } from 'react';
import {
  Badge,
  ErrorNote,
  IconButton,
  type Notify,
  Pager,
  Panel,
  jobStatusTone,
  useRun,
} from '../components/ui.js';
import { fmtBytes, fmtDuration, fmtWhen } from '../lib/format.js';
import { useServerPaged } from '../lib/usePaged.js';

export function Jobs({ client, notify }: { client: CommissionSightClient; notify: Notify }) {
  const run = useRun(notify);
  const fetchPage = useCallback(
    (offset: number, limit: number) => client.admin.listJobs({ offset, limit }),
    [client],
  );
  const {
    items: jobs,
    error,
    reload,
    offset,
    count,
    total,
    hasMore,
    next,
    prev,
  } = useServerPaged(fetchPage, 25);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminJobDetail | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadDetail = useCallback(
    async (id: string) => {
      setDetail(null);
      setDetailErr(null);
      setLoadingDetail(true);
      try {
        setDetail(await client.admin.jobDetail(id));
      } catch (e) {
        setDetailErr(e instanceof Error ? e.message : 'Failed to load job detail');
      } finally {
        setLoadingDetail(false);
      }
    },
    [client],
  );

  const toggle = (id: string) => {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    loadDetail(id);
  };

  const retry = (id: string) =>
    run(
      async () => {
        await client.admin.retryJob(id);
        reload();
        if (openId === id) await loadDetail(id);
      },
      { success: 'Job requeued' },
    );

  const rescore = (id: string) =>
    run(
      async () => {
        await client.admin.rescoreJob(id);
        reload();
        if (openId === id) await loadDetail(id);
      },
      { success: 'Re-score queued' },
    );

  return (
    <Panel title="Job inspector (all accounts)">
      {error && (
        <div className="mb-3">
          <ErrorNote message={error} onRetry={reload} />
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-neutral-500">
          <tr>
            <th className="py-1">Period</th>
            <th>Carrier</th>
            <th>Status</th>
            <th>Error</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <Fragment key={j.id}>
              <tr className="border-t border-neutral-100">
                <td className="py-2">
                  {formatPeriodLong({ year: j.periodYear, month: j.periodMonth })}
                </td>
                <td className="font-mono text-xs">{j.carrierId.slice(0, 8)}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <Badge tone={jobStatusTone(j.status)}>{j.status}</Badge>
                    {j.rescoreSuggested && (
                      <span title="An earlier baseline month was uploaded after this period was scored.">
                        <Badge tone="amber">stale</Badge>
                      </span>
                    )}
                  </div>
                </td>
                <td className="max-w-xs truncate text-xs text-rose-600">{j.error ?? ''}</td>
                <td className="whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <IconButton
                      icon={openId === j.id ? 'collapse' : 'view'}
                      label={openId === j.id ? 'Hide details' : 'Inspect'}
                      onClick={() => toggle(j.id)}
                    />
                    {j.rescoreSuggested && j.status === 'completed' && (
                      <IconButton
                        icon="rescore"
                        label="Re-score this period"
                        tone="amber"
                        onClick={() => rescore(j.id)}
                      />
                    )}
                    {j.status === 'failed' && (
                      <IconButton
                        icon="retry"
                        label="Retry job"
                        tone="emerald"
                        onClick={() => retry(j.id)}
                      />
                    )}
                  </div>
                </td>
              </tr>
              {openId === j.id && (
                <tr className="bg-neutral-50">
                  <td colSpan={5} className="px-4 py-4">
                    {loadingDetail ? (
                      <p className="text-sm text-neutral-500">Loading job detail…</p>
                    ) : detailErr ? (
                      <ErrorNote message={detailErr} onRetry={() => loadDetail(j.id)} />
                    ) : detail ? (
                      <JobDetailView
                        d={detail}
                        onRetry={() => retry(j.id)}
                        onRescore={() => rescore(j.id)}
                      />
                    ) : null}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      {jobs.length === 0 && !error && <p className="text-sm text-neutral-500">No jobs.</p>}
      <Pager
        offset={offset}
        count={count}
        total={total}
        hasMore={hasMore}
        onPrev={prev}
        onNext={next}
      />
    </Panel>
  );
}

function kv(label: string, value: ReactNode) {
  return (
    <div className="flex justify-between gap-4 py-0.5">
      <span className="text-neutral-500">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function JobDetailView({
  d,
  onRetry,
  onRescore,
}: { d: AdminJobDetail; onRetry: () => void; onRescore: () => void }) {
  const j = d.job;
  const stats = j.stats ?? {};
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {d.rescoreSuggested && (
        <div className="md:col-span-2 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="text-amber-900">
            <span className="font-semibold">Scoring is stale.</span> An earlier baseline month was
            uploaded after this period was scored. Re-score recomputes this period’s statuses
            against the current baseline (no re-upload, no effect on later periods).
          </p>
          <button
            type="button"
            onClick={onRescore}
            className="shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
          >
            Re-score this period
          </button>
        </div>
      )}
      <div className="rounded-lg border border-neutral-200 bg-white p-4 text-sm">
        <h4 className="mb-2 font-semibold">Job</h4>
        {kv('Job ID', <span className="font-mono text-xs">{j.id}</span>)}
        {kv('Status', <Badge tone={jobStatusTone(j.status)}>{j.status}</Badge>)}
        {kv('Account', d.account?.name ?? '—')}
        {kv('Carrier', d.carrierName ?? '—')}
        {kv('Created', fmtWhen(j.createdAt))}
        {kv('Started', fmtWhen(j.startedAt))}
        {kv('Completed', fmtWhen(j.completedAt))}
        {kv('Duration', fmtDuration(d.durationMs))}
      </div>
      <div className="rounded-lg border border-neutral-200 bg-white p-4 text-sm">
        <h4 className="mb-2 font-semibold">Source file & results</h4>
        {kv('File', d.file?.originalFilename ?? '—')}
        {kv('Size', fmtBytes(d.file?.byteSize))}
        {kv(
          'Checksum',
          <span className="font-mono text-[11px]">
            {d.file?.checksumSha256?.slice(0, 16) ?? '—'}…
          </span>,
        )}
        {kv('R2 key', <span className="break-all font-mono text-[11px]">{j.r2Key ?? '—'}</span>)}
        {Object.keys(stats).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {Object.entries(stats).map(([k, v]) => (
              <span
                key={k}
                className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-neutral-600"
              >
                {k}: {v}
              </span>
            ))}
          </div>
        )}
      </div>
      {j.error && <JobErrorPanel error={j.error} onRetry={onRetry} />}
    </div>
  );
}

function JobErrorPanel({ error, onRetry }: { error: string; onRetry: () => void }) {
  const ex = explainJobError(error);
  return (
    <div className="space-y-2 md:col-span-2">
      {ex && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-amber-200 px-1.5 py-0.5 font-mono text-[11px] font-medium text-amber-900">
              {ex.category}
            </span>
            <span className="font-semibold text-amber-900">{ex.title}</span>
            <span className="text-xs text-amber-700">
              · {ex.retryable ? 'retryable' : 'permanent'}
            </span>
          </div>
          <p className="mt-1.5 text-neutral-700">
            <span className="font-medium">Root cause:</span> {ex.adminHint}
          </p>
          <p className="mt-1 text-neutral-600">
            <span className="font-medium">What the user sees:</span> {ex.userMessage}
          </p>
        </div>
      )}
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
        <div className="mb-1 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-rose-800">Raw error</h4>
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-medium text-emerald-600 hover:underline"
          >
            Retry job
          </button>
        </div>
        <pre className="overflow-auto whitespace-pre-wrap break-words text-xs text-rose-700">
          {error}
        </pre>
      </div>
    </div>
  );
}
